<template>
  <ArchiveModal
    :open="open"
    :title="cabinet ? `Uploads to ${cabinet.name}` : 'Uploads'"
    subtitle="Choose when people can upload files to this cabinet."
    size="md"
    @close="$emit('close')"
  >
    <form v-if="cabinet" class="ar-uploads-form" @submit.prevent="save">
      <div class="ar-mode-list" role="radiogroup" aria-label="Uploads">
        <label v-for="m in modes" :key="m.key" class="ar-mode" :class="[m.key, { on: form.mode === m.key }]">
          <input v-model="form.mode" type="radio" name="ar-upload-mode" :value="m.key" class="sr-only" />
          <span class="ar-mode-icon" aria-hidden="true"><i class="bi" :class="m.icon"></i></span>
          <span class="ar-mode-text">
            <strong>{{ m.label }}</strong>
            <small>{{ m.hint }}</small>
          </span>
          <i class="bi ar-mode-check" :class="form.mode === m.key ? 'bi-check-circle-fill' : 'bi-circle'" aria-hidden="true"></i>
        </label>
      </div>

      <section v-if="form.mode === 'scheduled'" class="ar-schedule" aria-label="Schedule">
        <div class="ar-schedule-grid">
          <div>
            <label for="ar-upl-opens" class="ar-label">Opens <span class="ar-optional">(optional)</span></label>
            <input id="ar-upl-opens" v-model="form.opens" type="datetime-local" class="form-control" />
            <small class="ar-muted">Empty: open from now.</small>
          </div>
          <div>
            <label for="ar-upl-closes" class="ar-label">Closes <span class="ar-optional">(optional)</span></label>
            <input id="ar-upl-closes" v-model="form.closes" type="datetime-local" class="form-control" :min="form.opens || nowInput" />
            <small class="ar-muted">Empty: stays open.</small>
          </div>
        </div>
        <div class="ar-chips" role="group" aria-label="Quick deadlines">
          <button
            v-for="p in presets"
            :key="p.label"
            type="button"
            class="ar-chip"
            :class="{ on: !form.opens && form.closes === p.closes.format(INPUT) }"
            @click="usePreset(p)"
          >
            {{ p.label }}
          </button>
        </div>
        <p v-if="scheduleError" class="ar-field-error mb-0">{{ scheduleError }}</p>
        <p v-else class="ar-schedule-summary">
          <i class="bi bi-calendar-range" aria-hidden="true"></i>
          <span>{{ scheduleSummary }}</span>
        </p>
      </section>

      <div v-if="form.mode !== 'open'" class="mt-3">
        <label for="ar-upl-note" class="ar-label">Message for uploaders <span class="ar-optional">(optional)</span></label>
        <textarea
          id="ar-upl-note"
          v-model="form.note"
          class="form-control"
          rows="2"
          maxlength="255"
          :placeholder="form.mode === 'closed' ? 'e.g. Closed for the year-end audit. Email Records for urgent files.' : 'e.g. For Q3 disbursement vouchers only.'"
        ></textarea>
        <small class="ar-muted">Shown on the cabinet, and to anyone who tries to upload while uploads are closed.</small>
      </div>

      <p class="ar-note mt-3 mb-0">
        <i class="bi bi-info-circle" aria-hidden="true"></i>
        <span>
          While uploads are closed nobody can upload, managers included. Files still uploading when they close aren't
          saved. Folders, moving, notes and the Trash keep working.
        </span>
      </p>
      <p v-if="setLine" class="ar-muted mt-2 mb-0">{{ setLine }}</p>
    </form>
    <template #footer>
      <button type="button" class="btn btn-light" @click="$emit('close')">Cancel</button>
      <button type="button" class="btn btn-primary" :disabled="busy || !!scheduleError" @click="save">
        <span v-if="busy" class="spinner-border spinner-border-sm mr-1"></span>{{ saveLabel }}
      </button>
    </template>
  </ArchiveModal>
</template>

<script>
import axios from "axios";
import moment from "moment";
import ArchiveModal from "./ArchiveModal.vue";
import { errorMessage, formatDateTime, whenText } from "../archiveHelpers";

const INPUT = "YYYY-MM-DDTHH:mm";

const MODES = [
  { key: "open", icon: "bi-unlock", label: "Open", hint: "People with upload access can upload any time." },
  { key: "closed", icon: "bi-lock", label: "Closed", hint: "Nobody can upload, managers included, until it's opened again." },
  { key: "scheduled", icon: "bi-calendar-range", label: "Scheduled", hint: "Opens and closes by itself at the times you set." },
];

/**
 * Open or close uploads to a cabinet, or schedule them (its managers and
 * Archives administrators). Emits saved(cabinet) with the updated cabinet.
 */
export default {
  name: "ArchiveUploadsModal",
  components: { ArchiveModal },
  props: {
    open: { type: Boolean, default: false },
    // { id, name, uploads: { mode, opens_at, closes_at, note, set_by_name, set_at } }
    cabinet: { type: Object, default: null },
  },
  data() {
    return { INPUT, modes: MODES, form: { mode: "open", opens: "", closes: "", note: "" }, busy: false, now: Date.now() };
  },
  computed: {
    uploads() {
      return (this.cabinet && this.cabinet.uploads) || {};
    },
    nowInput() {
      return moment(this.now).format(INPUT);
    },
    opensAt() {
      return this.form.opens ? moment(this.form.opens, INPUT) : null;
    },
    closesAt() {
      return this.form.closes ? moment(this.form.closes, INPUT) : null;
    },
    // The closing time already saved (it may be past: then only the message is being changed).
    keepsClose() {
      return !!this.uploads.closes_at && this.uploads.mode === "scheduled" && this.form.closes === moment(this.uploads.closes_at).format(INPUT);
    },
    scheduleError() {
      if (this.form.mode !== "scheduled") return "";
      if (!this.opensAt && !this.closesAt) return "Set when uploads open, when they close, or both.";
      if ((this.opensAt && !this.opensAt.isValid()) || (this.closesAt && !this.closesAt.isValid())) return "Enter a real date and time.";
      if (this.opensAt && this.closesAt && !this.closesAt.isAfter(this.opensAt)) return "Uploads have to close after they open.";
      if (this.closesAt && !this.closesAt.isAfter(moment(this.now)) && !this.keepsClose) {
        return "That closing time has already passed. Pick a later one, or choose Closed.";
      }
      return "";
    },
    scheduleSummary() {
      const now = moment(this.now);
      const opensLater = this.opensAt && this.opensAt.isAfter(now);
      const when = (m) => whenText(m.toDate(), { now: this.now });
      if (this.closesAt && !this.closesAt.isAfter(now)) return `Uploads closed ${when(this.closesAt)} and stay closed.`;
      if (opensLater && this.closesAt) return `Closed until ${when(this.opensAt)}, then open until ${when(this.closesAt)}.`;
      if (opensLater) return `Closed until ${when(this.opensAt)}, then open with no end.`;
      if (this.closesAt) return `Open now, until ${when(this.closesAt)}. Then closed.`;
      return "Open now, with no end.";
    },
    // Quick deadlines (open from now until 5:00 PM that day), without repeats or past times.
    presets() {
      const now = moment(this.now);
      const at5 = (m) => m.clone().hour(17).minute(0).second(0).millisecond(0);
      const friday = at5(now.clone().day(5));
      if (!friday.isAfter(now)) friday.add(7, "days");
      const week = at5(now.clone().add(7, "days"));
      const monthEnd = at5(now.clone().endOf("month"));
      const seen = {};
      return [
        { label: "Until 5:00 PM today", closes: at5(now) },
        { label: `Until ${friday.format("ddd, MMM D")}, 5:00 PM`, closes: friday },
        { label: `For a week (to ${week.format("MMM D")})`, closes: week },
        { label: `Until month end (${monthEnd.format("MMM D")})`, closes: monthEnd },
      ].filter((p) => {
        const key = p.closes.format(INPUT);
        if (!p.closes.isAfter(now) || seen[key]) return false;
        seen[key] = true;
        return true;
      });
    },
    saveLabel() {
      return { open: "Open uploads", closed: "Close uploads", scheduled: "Save schedule" }[this.form.mode];
    },
    setLine() {
      if (!this.uploads.set_at) return "";
      return `Last changed${this.uploads.set_by_name ? ` by ${this.uploads.set_by_name}` : ""} on ${formatDateTime(this.uploads.set_at)}.`;
    },
  },
  watch: {
    open: {
      immediate: true,
      handler(value) {
        if (!value || !this.cabinet) return;
        this.now = Date.now();
        const u = this.uploads;
        this.form = {
          mode: u.mode || "open",
          opens: u.opens_at ? moment(u.opens_at).format(INPUT) : "",
          closes: u.closes_at ? moment(u.closes_at).format(INPUT) : "",
          note: u.note || "",
        };
      },
    },
  },
  methods: {
    usePreset(preset) {
      this.form.opens = "";
      this.form.closes = preset.closes.format(INPUT);
    },
    async save() {
      if (this.busy || this.scheduleError) return;
      this.busy = true;
      const scheduled = this.form.mode === "scheduled";
      try {
        const { data } = await axios.put(`api/archives/cabinets/${this.cabinet.id}/uploads`, {
          mode: this.form.mode,
          opens_at: scheduled && this.opensAt ? this.opensAt.toISOString() : null,
          closes_at: scheduled && this.closesAt ? this.closesAt.toISOString() : null,
          note: this.form.mode === "open" ? null : this.form.note.trim() || null,
        });
        toastr.success(data.message, "Uploads");
        this.$emit("saved", data.cabinet);
        this.$emit("close");
      } catch (err) {
        toastr.error(await errorMessage(err, "The change wasn't saved."), "Uploads");
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>
